<template>
  <div class="accounts-page page">
    <div class="page-header">
      <h1 class="title">Accounts</h1>
      <p class="subtitle">
        Review your accounts and resolve any issues you might be having with them.
      </p>
    </div>

    <div class="alerts">
      <el-alert v-if="errors" title="Issue detected, stay calm tho!" type="error" effect="dark" :closable="true">
        It looks like {{ errors }} of your accounts are experiencing some kind of error. Sometimes it's a "Security Checkpoint", and other times it just means you've changed your password and need to login again.
        <br><br>
        Simply click "Fix Issue" on the affected account below to resolve the issue.
      </el-alert>
    </div>

    <el-row>
      <el-col>

        <div v-if="!allAccounts.length" class="empty">
          <div>
            <p>You currently don't have any accounts available.</p>
          </div>
          <el-button type="primary" @click="navigate(Pages.AddAccount)">Add an account</el-button>
        </div>

        <el-card
          v-loading="isLoading"
          v-for="account in allAccounts"
          :key="account.id"
          class="card-dialog account">

          <div class="body info">
            <!-- Removing profile pic for now -->
            <!-- <img :src="account.profile.profile_pic_url" class="profile-src"/> -->
            <strong target="_blank" class="username">@{{ account.profile.username }}</strong>
            <div class="status" :class="!account.needsLogin && !account.likeBlocked ? 'success' : 'danger'">{{ account.needsLogin || account.likeBlocked ? 'Logged out' : 'Active' }}</div>
          </div>
          <div class="footer actions">
            <el-button @click="removeAccount(account.user_id)">
              Remove
            </el-button>
            <el-button v-if="account.needsLogin || account.likeBlocked" @click="showLoginWindow(account.profile.username)">
              Fix Issue
            </el-button>
          </div>

        </el-card>
        
      </el-col>
    </el-row>

    <div v-if="allAccounts.length && (allAccounts.length < maxAccounts)" class="account-actions">
      <el-button type="primary" @click="navigate(Pages.AddAccount)">Add an account</el-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { BridgeAPI } from '../bridge';

export default {
  name: 'Accounts',
  data () {
    return {
      isLoading: false,
    }
  },
  async created () {
    await this.loadAccounts().catch(() => false);
    await BridgeAPI.getAllAccounts()
  },
  computed: {
    ...mapGetters([
      'allAccounts',
      'Pages',
      'maxAccounts',
    ]),
    errors () {
      return this.allAccounts.filter(a => a.needsLogin || a.likeBlocked).length || 0;
    },
    accountCounter () {
      let c = this.allAccounts.length;
      let out = {
        type: 'success',
        message: ''
      };
      if (c === 0) {
        out.message = `You've haven't added any accounts yet! You can add up to 5 users in total.`
      } else if (c >= 1) {
        out.message = `You've added ${c} account(s). You can add ${this.maxAccounts - c} more to earn the maximum amount of gold!`
      }
      if (c === (this.maxAccounts - 1)) {
        out.type = 'warning';
        out.message = `You've added ${c} account(s). You can only add ${this.maxAccounts - c} more!`
      } else if (c >= this.maxAccounts) {
        out.type = 'error';
        out.message = `You've met the maximum number of accounts you can have in this app. We want to keep your account safe, and unfortunately limiting the number of accounts you can add is part of that.`
      }
      return out;
    }
  },
  methods: {
    ...mapActions({
      loadAccounts: 'loadAccounts',
      navigate: 'changePage'
    }),
    async removeAccount (userId) {
      this.isLoading = true;
      BridgeAPI.removeAccount(userId);
      await this.loadAccounts().catch(() => {});
      this.isLoading = false;
    },
    showLoginWindow (username) {
      const h = this.$createElement;
      let msg = `Since Likezoid doesn't store your username and password on your computer or our servers, we can't do this for you.`;

      this.$msgbox({
        title: 'Resolve Account Issue',
        message: h('div', null, [
          h('p', { style: 'text-align:left'}, [
            h('span', null, [
              'In order to solve the issue, you need to re-login to the account: ',
              h('strong', null, `${username}.`)
            ]),
            h('br', null, null),
            h('br', null, null),
            h('span', null, msg),
          ]),
          h('br', null, null),
          h('p', { style: 'text-align:left'}, [
            h('span', { style: 'text-align:left'}, `Simply click the login button below and follow the directions on the page and the issue should resolve itself as soon as you login.`)
          ]),
        ]),
        roundButton: true,
        center: true,
        showCancelButton: true,
        confirmButtonText: `Login to @${username} now`,
        cancelButtonText: 'Cancel'
      })
        .then(() => {
          // make this open the secret window for this account... next update
          this.navigate(this.Pages.AddAccount);
        }).catch(() => {})
    }
  },
}
</script>

<style lang="scss" scoped>
.accounts-page {
  padding-left: 87.5px;
  padding-right: 87.5px;

  .page-header .subtitle {
    width: 342px;
  }

  .alerts {
    padding-left: 22px;
    padding-right: 22px;
  }

  .el-alert {
    padding: 28px 28px 28px 247px;
    font-size: 14px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    ::v-deep .el-alert__title {
      font-size: 19px;
      font-weight: 600;
      line-height: 38px;

      &+.el-alert__description {
        margin: 0;
        line-height: 19px;
      }
    }

    &.alert-free-likes {
      background-image: url('../assets/img/view/account/banner/free-likes.svg');
    }

    &--error {
      background-image: url('../assets/img/view/account/banner/issue.svg');
    }
  }

  .el-alert, .el-card.account {
    margin-bottom: 20px;
  }

  .el-row {
    overflow: hidden;

    .el-col {
      display: flex;
      flex-flow: row wrap;
      justify-content: center;

      .empty {
        text-align: center;

        p {
          color: #86899c;
          margin-top: 0;
          margin-bottom: 40px;
        }
      }

      .el-card.account {
        margin-left: 6.5px;
        margin-right: 6.5px;
        padding: 0;
        width: 30%;

        .info {
          padding: 27px 10px;
          display: flex;
          flex-direction: column;
          align-items: center;

          .profile-src {
            margin-bottom: 16px;
            width: 62px;
            height: 62px;
          }

          .username {
            color: #3A3B4e;
            font-size: 15px;
            font-weight: 600;
            line-height: 23px;
          }

          .status {
            font-size: 10px;
            font-weight: 600;
            line-height: 23px;
            text-transform: uppercase;

            &:before {
              content: '';
              display: inline-block;
              margin-right: 7px;
              width: 7px;
              height: 7px;
              color: #3A3B4e;
              background: #3A3B4e;
              border-radius: 3.5px;
            }

            &.success {
              color: #bad864;

              &:before {
                background: #bad864;
              }
            }

            &.danger {
              color: #e88473;

              &:before {
                background: #e88473;
              }
            }
          }
        }

        .actions {
          display: flex;
          padding: 10px 10px 14px;
          justify-content: center;

          .el-button {
            font-size: 11.5px;
          }
        }
      }
    }
  }

  .account-actions {
    margin-top: 27px;
    text-align: center;
  }
}
</style>
